/**
 * Reload in a way that can actually pick up a new build.
 *
 * A plain location.reload() is not enough once a service worker is installed.
 * sw.js answers navigations from a PRECACHED index.html, so the reload's own
 * document request is served by the worker and comes back as the same stale
 * HTML, asking for the same hashed chunk that no longer exists.
 *
 * /assets/* is immutable and purged on deploy, so a client holding old HTML is
 * asking for files that are genuinely gone (verified: entry and lazy chunks
 * from earlier deploys return 404). The only cure is to drop the worker and
 * its caches first, then reload.
 *
 * This lives here, rather than beside the one boundary that first needed it,
 * because every "Reload" the app offers has to do this. Two of them did not:
 * the lazy dashboard's stalled-loader button and the router's error card both
 * called location.reload() directly, which is the one thing guaranteed not to
 * help. A user told to reload, who reloads, and lands on the same screen, has
 * been given a button that cannot work.
 *
 * Best-effort throughout: every step is wrapped, and the reload happens
 * regardless, because a reload without the cleanup is still better than none.
 */
export async function hardReload(): Promise<void> {
  // Offline, the precache is the only copy of the app there is, and wiping it
  // would turn a slow load into a dead one with nothing left to serve. The
  // stalled loader that calls this fires on a ten-second timeout, and a flaky
  // connection is one of the likelier reasons it fires, so this is not a rare
  // corner. A plain reload is served from that precache and still gives the
  // user their best chance; the stale-build cleanup can wait until there is a
  // network to fetch a new build from.
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    window.location.reload();
    return;
  }
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister().catch(() => false)));
    }
  } catch { /* worker API unavailable or blocked */ }
  try {
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k).catch(() => false)));
    }
  } catch { /* Cache API unavailable */ }
  window.location.reload();
}
